import React from 'react'
import { Columns } from '../columns'
import { Column, ExtraType } from '../types/table'
import { DailyBudgetChange } from '../components/campaigns/DailyBudgetChange'
import { DefaultBidChange } from '../components/ad_groups/DefaultBidChange'
import { KeywordBidChange } from '../components/keywords/KeywordBidChange'
import { TargetBidChange } from '../components/targets/TargetBidChange'

import { AdType } from './widgets'
import { DefaultPerformanceColumns } from '../components/table/default_performance_columns'
import { KDPPerformanceColumns } from '../components/table/kdp_performance_columns'

export const disableInputs = (columns: Array<Column<any, any>>) => {
    return columns.map((column) => {
        switch (column.key) {
            case 'smart_bid':
                return {
                    ...column,
                    cell: (campaign: any, extra: ExtraType) =>
                        column?.cell?.(campaign, extra, true) ?? undefined,
                }
            case 'daily_budget':
            case 'default_bid':
            case 'bid':
                return {
                    ...column,
                    cell: (campaign: any, extra: ExtraType) =>
                        column?.cell?.(campaign, extra, true) ?? undefined,
                }
            case 'target_acos':
                return {
                    ...column,
                    cell: (product: any, { mutateItems, props }: ExtraType) =>
                        column?.cell?.(product, { mutateItems, props }, true) ?? undefined,
                }
            default:
                return column
        }
    })
}

export class ColumnGenerator {
    private adType: number
    private isKdp: boolean

    static readonly AdType = AdType

    constructor(adType: number, isKdp: boolean = false) {
        this.adType = adType
        this.isKdp = isKdp
    }

    /**
     * Generates all available columns for ad type.
     * @return {Column[]} All available columns for ad type.
     */
    public generateAvailable<T>(): Column<T, any>[] {
        if (!this.isValidAdType()) {
            throw new Error('[columnResolver] ad type not supported: ' + this.adType)
        }

        let hasTacos = false
        let hasKDP = false

        let columns = [] as Column<T, any>[]
        switch (this.adType) {
            case AdType.Campaign:
                hasTacos = true
                hasKDP = true

                columns = [
                    Columns.CampaignName,
                    Columns.SmartBidToggle,
                    Columns.StartDate,
                    Columns.EndDate,
                    Columns.TargetedProducts,
                    {
                        ...Columns.DailyBudget,
                        cell: (campaign, extra, disabled = false) => (
                            <DailyBudgetChange
                                disabled={disabled}
                                campaign={campaign}
                                extra={extra}
                            />
                        ),
                    },
                    Columns.LastDailyBudgetChange,
                    Columns.CPC,
                    Columns.TargetCPC,
                    ...DefaultPerformanceColumns,
                ]
                break
            case AdType.AdGroup:
                hasTacos = true
                hasKDP = true

                columns = [
                    Columns.AdGroupName,
                    Columns.AdGroupBreadcrumbs,
                    Columns.TargetedProducts,
                    {
                        ...Columns.DefaultBid,
                        cell: (ad_group, extra, disabled: boolean = false) => (
                            <DefaultBidChange
                                disabled={disabled ?? false}
                                ad_group={ad_group}
                                extra={extra}
                            />
                        ),
                    },
                    Columns.LastDefaultBidChange,
                    Columns.CPC,
                    Columns.TargetCPC,
                    ...DefaultPerformanceColumns,
                ]
                break
            case AdType.Keyword:
                hasTacos = true
                hasKDP = true

                columns = [
                    {
                        ...Columns.Keyword,
                        props: {
                            link: true,
                            smartBidSettings: true,
                        },
                    },
                    Columns.Parent,
                    Columns.TargetedProducts,
                    {
                        ...Columns.Bid,
                        cell: (keyword, extra) => (
                            <KeywordBidChange keyword={keyword} extra={extra} />
                        ),
                    },
                    Columns.LastBidChange,
                    Columns.CPC,
                    Columns.TargetCPC,
                    ...DefaultPerformanceColumns,
                ]
                break
            case AdType.NegativeKeyword:
                columns = [Columns.Parent, Columns.Keyword, Columns.TargetedProducts]
                break
            case AdType.CampaignNegativeKeyword:
                columns = [
                    Columns.Keyword,
                    {
                        ...Columns.TargetedProducts,
                        key: 'campaign',
                    },
                ]
                break
            case AdType.ProductAd:
                hasTacos = true
                hasKDP = true

                columns = [
                    // @ts-ignore
                    Columns.ASIN,
                    Columns.Parent,
                    // @ts-ignore
                    Columns.CPC,
                    Columns.TargetCPC,
                    // @ts-ignore
                    ...DefaultPerformanceColumns,
                ]
                break
            case AdType.Target:
                hasTacos = true
                hasKDP = true

                columns = [
                    {
                        ...Columns.TargetingExpression,
                        props: {
                            link: true,
                            smartBidSettings: true,
                        },
                    },
                    Columns.Parent,
                    Columns.TargetedProducts,
                    {
                        ...Columns.Bid,
                        // @ts-ignore
                        cell: (target, extra, disabled = false) => (
                            <TargetBidChange disabled={disabled} target={target} extra={extra} />
                        ),
                    },
                    Columns.LastBidChange,
                    Columns.CPC,
                    Columns.TargetCPC,
                    ...DefaultPerformanceColumns,
                ]
                break
            case AdType.NegativeTarget:
                columns = [Columns.Parent, Columns.TargetingExpression, Columns.TargetedProducts]
                break
            default:
                throw new Error('[columnResolver] ad type not supported ' + this.adType)
        }

        if (hasTacos) {
            // append TACOS right before ACOS
            const acosIndex = columns.findIndex((col) => col.key === 'acos')
            // this was for product ads, not sure if it's needed
            // @todo check if pads needs this
            // acosIndex = columns.findIndex((col) => typeof col === 'string' ? col === 'acos' : col.key === 'acos')
            columns.splice(acosIndex, 0, {
                ...Columns.TargetACOS,
                cell: (product, { mutateItems, props }, disabled = false) =>
                    Columns.TargetACOS.cell(
                        product,
                        {
                            mutateItems,
                            props: {
                                ...props,
                                target: 'campaign',
                            },
                        },
                        (disabled = false),
                    ),
            })
        }

        // Optionally add KDP columns
        if (hasKDP && this.isKdp) {
            columns.push(...KDPPerformanceColumns)
        }

        return columns
    }

    private isValidAdType(): boolean {
        return Object.values(AdType).includes(this.adType)
    }
}
